import JSPDF from 'jspdf'
import logoDutton from '@/assets/images/logo/logo_text.jpg'
import logoDutton2 from '@/assets/images/logo/logo_text_2.jpg'
import { formatCurrency, formatNumber } from '@/@core/utils/filter'
import { buildStorageUrl } from '@/utils/urls'
import COMPANY from '@/utils/config'

export default class OrderGenerator {
  constructor(orderData) {
    this.orderData = orderData
    this.doc = new JSPDF('p', 'mm', 'letter')
    this.pageHeight = 297.4
    this.marginBottom = 20
    this.currencySymbol = (this.orderData.CurrencyInfo && this.orderData.CurrencyInfo.symbol) ? this.orderData.CurrencyInfo.symbol : 'Q'
  }

  // === Helpers (static para evitar class-methods-use-this) ===
  static async fetchAsDataURL(url) {
    const res = await fetch(url, { mode: 'cors' })
    const blob = await res.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result) // dataURL
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  static getImageTypeFromDataURL(dataUrl, fallback = 'JPEG') {
    const m = /^data:(image\/(png|jpeg|jpg));base64,/i.exec(dataUrl || '')
    const kind = m ? m[2].toUpperCase() : fallback
    return kind === 'PNG' ? 'PNG' : 'JPEG'
  }

  async generate() {
    await this.addHeader()
    await this.addCustomerInfo()
    const finalY = await this.addProductsTable()
    await this.addFooter(finalY)

    // Optimizar el PDF
    return this.doc.output('blob', { compress: true, putOnlyUsedFonts: true })
  }

  checkPageBreak(y, requiredHeight = 0) {
    if (y + requiredHeight > this.pageHeight - this.marginBottom) {
      this.doc.addPage()
      return -1
    }
    return y
  }

  formatCurrencyWithSymbol(value) {
    const formatted = formatNumber(value, 2)
    return formatted ? `${this.currencySymbol}.${formatted}` : ''
  }

  async addHeader() {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const img2 = new Image()
      img.src = logoDutton
      img2.src = logoDutton2

      img.onload = () => {
        // Logos
        this.doc.addImage(logoDutton, 'JPEG', 12, 8, 40, 20, undefined, 'FAST')
        this.doc.addImage(logoDutton2, 'JPEG', 145, 15, 60, 10, undefined, 'FAST')

        // Datos de la empresa (desde env/config)
        this.doc.setFontSize(8)
        this.doc.text(`Correo: ${COMPANY.EMAIL}`, 12, 40)
        this.doc.text(`Teléfonos: ${COMPANY.PHONE}`, 12, 45)
        this.doc.text(`Dirección: ${COMPANY.ADDRESS}`, 12, 50)
        resolve()
      }

      img.onerror = reject
    })
  }

  async addCustomerInfo() {
    const nitCliente = (this.orderData.CustomerInfo.nit_cf === 'Y')
      ? `Consumidor final - ${this.orderData.CustomerInfo.nit}`
      : this.orderData.CustomerInfo.nit

    // Sección de información del cliente
    const startY = 57
    const boxHeight = 38

    // Título con fondo azul
    this.doc.setFillColor(18, 64, 111)
    this.doc.rect(10, startY, 195, 7, 'F')

    // Texto del título en blanco
    this.doc.setTextColor(255, 255, 255)
    this.doc.setFontSize(10)
    this.doc.setFont('helvetica', 'bold')
    this.doc.text('INFORMACIÓN DEL CLIENTE', 15, startY + 5)

    // Fondo gris claro para el contenido
    this.doc.setFillColor(245, 245, 245)
    this.doc.rect(10, startY + 7, 195, boxHeight - 7, 'F')

    // Borde sutil alrededor
    this.doc.setDrawColor(180, 180, 180)
    this.doc.setLineWidth(0.1)
    this.doc.rect(10, startY, 195, boxHeight, 'S')

    // Contenido
    this.doc.setTextColor(0, 0, 0)
    this.doc.setFont('helvetica', 'bold')
    this.doc.text(`${this.orderData.CustomerInfo.name}`, 15, startY + 14)

    this.doc.setFontSize(8)
    this.doc.setFont('helvetica', 'normal')
    this.doc.text(`Contacto: ${this.orderData.Vendedor} / ${this.orderData.VendedorEmail || 'No disponible'}`, 15, startY + 20)
    this.doc.text(`NIT: ${nitCliente}`, 15, startY + 25)
    this.doc.text(`Teléfonos: ${this.orderData.CustomerInfo.phone} / ${this.orderData.CustomerInfo.cellphone}`, 15, startY + 30)

    // Dirección con manejo de texto largo
    const maxWidth = 140
    const direccion = `Dirección: ${this.orderData.DireccionEnvio}`
    const splitDireccion = this.doc.splitTextToSize(direccion, maxWidth)
    this.doc.text(splitDireccion, 15, startY + 35)

    // Datos del pedido alineados a la derecha
    this.doc.text(`Pedido No: #${this.orderData.Id}`, 165, startY + 20)
    this.doc.text(`Fecha: ${this.orderData.FechaPedidoCliente}`, 165, startY + 25)
    this.doc.text(`Estado: ${this.orderData.Estado}`, 165, startY + 30)
  }

  async addProductsTable() {
    let y = 100

    if (this.orderData.Detalle.length > 0) {
      // Configuración de la tabla
      const startX = 10
      const tableWidth = 195
      const colWidths = [30, 95, 20, 25, 25]
      const rowHeight = 12

      // Color y grosor de líneas
      this.doc.setDrawColor(180, 180, 180)
      this.doc.setLineWidth(0.1)

      // Encabezado
      this.doc.setFillColor(18, 64, 111)
      this.doc.rect(startX, y, tableWidth, rowHeight, 'F')

      // Texto del encabezado
      this.doc.setTextColor(255, 255, 255)
      this.doc.setFontSize(8)
      this.doc.setFont('helvetica', 'bold')

      let xPos = startX
      const headers = ['REF.', 'DESCRIPCIÓN', 'CANT.', 'PRECIO', 'TOTAL']
      for (let i = 0; i < headers.length; i += 1) {
        const header = headers[i]
        this.doc.text(header, xPos + 3, y + 7)
        xPos += colWidths[i]
      }

      y += rowHeight

      // Contenido de la tabla
      this.doc.setTextColor(0, 0, 0)
      this.doc.setFont('helvetica', 'normal')
      this.doc.setFontSize(7.5)

      // Prefetch de imágenes en paralelo como dataURL (evitar CORS)
      const imagesDataUrl = await Promise.all(
        this.orderData.Detalle.map(p => {
          if (p.producto_imagen && p.producto_imagen.path) {
            const url = buildStorageUrl(p.producto_imagen.path)
            return OrderGenerator.fetchAsDataURL(url).catch(() => null)
          }
          return Promise.resolve(null)
        }),
      )
      let headerY = y - rowHeight

      // Recorremos sin await dentro del loop ni for-of
      for (let i = 0; i < this.orderData.Detalle.length; i += 1) {
        const producto = this.orderData.Detalle[i]

        const newY = this.checkPageBreak(y, rowHeight)
        const isNewPage = newY < 0

        if (isNewPage) {
          y = 10
          this.doc.setFillColor(18, 64, 111)
          this.doc.rect(startX, y, tableWidth, rowHeight, 'F')

          this.doc.setTextColor(255, 255, 255)
          this.doc.setFontSize(8)
          this.doc.setFont('helvetica', 'bold')

          xPos = startX
          for (let idx = 0; idx < headers.length; idx += 1) {
            const header = headers[idx]
            this.doc.text(header, xPos + 3, y + 7)
            xPos += colWidths[idx]
          }

          y += rowHeight
          headerY = y - rowHeight

          let xLine = startX
          const currentHeaderY = headerY
          const currentY = y
          for (let idx = 0; idx < colWidths.length; idx += 1) {
            const width = colWidths[idx]
            this.doc.line(xLine, currentHeaderY, xLine, currentY)
            xLine += width
          }
          this.doc.line(xLine, currentHeaderY, xLine, currentY)

          this.doc.setTextColor(0, 0, 0)
          this.doc.setFontSize(7.5)
          this.doc.setFont('helvetica', 'normal')
        } else {
          y = newY
        }

        // Línea horizontal después de cada fila
        this.doc.line(startX, y, startX + tableWidth, y)

        // Imagen si existe
        const dataUrl = imagesDataUrl[i]
        if (dataUrl) {
          try {
            const type = OrderGenerator.getImageTypeFromDataURL(dataUrl, 'JPEG')
            this.doc.addImage(
              dataUrl,
              type,
              startX + colWidths[0] + 3,
              y + 0.5,
              11.5,
              11.5,
              undefined,
              'FAST',
            )
          } catch (error) {
            // continuar sin imagen
          }
        }

        // Información del producto
        let xText = startX
        // REF
        this.doc.text(String(producto.sku), xText + 3, y + 7)
        xText += colWidths[0]

        // Descripción (después de la imagen si existe)
        const maxDescWidth = colWidths[1] - 25
        const descripcion = producto.producto_desc || ''
        const splitDesc = this.doc.splitTextToSize(descripcion, maxDescWidth)
        this.doc.text(splitDesc, xText + 22, y + 7)
        xText += colWidths[1]

        // Cantidad
        this.doc.text(formatNumber(producto.cantidad, 0), xText + 3, y + 7)
        xText += colWidths[2]

        // Precio (usar precio_pedido)
        this.doc.text(this.formatCurrencyWithSymbol(producto.precio_pedido), xText + 3, y + 7)
        xText += colWidths[3]

        // Total
        this.doc.text(formatCurrency(producto.total), xText + 3, y + 7)
        y += rowHeight

        let xLine = startX
        const currentY = y
        const prevY = y - rowHeight
        for (let idx = 0; idx < colWidths.length; idx += 1) {
          const width = colWidths[idx]
          this.doc.line(xLine, prevY, xLine, currentY)
          xLine += width
        }
        this.doc.line(xLine, prevY, xLine, currentY)
      }

      // Línea final de la tabla
      this.doc.line(startX, y, startX + tableWidth, y)

      y = this.checkPageBreak(y, 15)

      // Totales
      y += 2
      const { total } = this.orderData.SummaryInfo[0]
      const totalFinal = parseFloat(total)

      // Cuadro para totales
      const totalesWidth = 50
      const totalesHeight = 8
      const totalesX = startX + tableWidth - totalesWidth

      // Fondo gris claro para totales
      this.doc.setFillColor(245, 245, 245)
      this.doc.rect(totalesX, y, totalesWidth, totalesHeight, 'F')

      // Borde sutil
      this.doc.setDrawColor(180, 180, 180)
      this.doc.setLineWidth(0.1)
      this.doc.rect(totalesX, y, totalesWidth, totalesHeight, 'S')

      // Textos de totales
      const textX = totalesX + 5
      const valoresX = totalesX + totalesWidth - 5

      this.doc.setTextColor(0, 0, 0)
      this.doc.setFont('helvetica', 'bold')
      this.doc.setFontSize(8)
      this.doc.text('TOTAL:', textX, y + 6)

      // Valor alineado a la derecha
      this.doc.text(formatCurrency(totalFinal), valoresX, y + 6, { align: 'right' })

      // Ahorro por ofertas vigentes, A LA PAR del total: aprovecha el espacio
      // libre a la izquierda y no agrega alto, que era lo que empujaba el
      // recuadro contra el pie de pagina. Solo aparece si hubo ahorro.
      const ahorro = (this.orderData.Detalle || []).reduce(
        (sum, p) => sum + (parseFloat(p.ahorro_total) || 0), 0,
      )

      if (ahorro > 0) {
        const sinOferta = totalFinal + ahorro
        const ahorroWidth = totalesX - startX - 4

        this.doc.setFillColor(232, 245, 233)
        this.doc.rect(startX, y, ahorroWidth, totalesHeight, 'F')
        this.doc.setDrawColor(76, 175, 80)
        this.doc.setLineWidth(0.2)
        this.doc.rect(startX, y, ahorroWidth, totalesHeight, 'S')

        const listaX = startX + 4
        const ahorroLabelX = startX + ahorroWidth * 0.52
        const ahorroValorX = startX + ahorroWidth - 4

        this.doc.setFont('helvetica', 'bold')
        this.doc.setFontSize(8)
        this.doc.setTextColor(90, 90, 90)
        this.doc.text('PRECIO DE LISTA:', listaX, y + 5.5)
        this.doc.text(formatCurrency(sinOferta), listaX + this.doc.getTextWidth('PRECIO DE LISTA:') + 3, y + 5.5)

        this.doc.setFont('helvetica', 'bold')
        this.doc.setFontSize(8)
        this.doc.setTextColor(46, 125, 50)
        this.doc.text('USTED AHORRA:', ahorroLabelX, y + 5.5)
        this.doc.text(formatCurrency(ahorro), ahorroValorX, y + 5.5, { align: 'right' })

        this.doc.setTextColor(0, 0, 0)
      }

      y += totalesHeight + 5

      // Agregar nota sobre IVA incluido
      this.doc.setFont('helvetica', 'normal')
      this.doc.setFontSize(8)
      this.doc.setTextColor(100, 100, 100) // Color gris para la nota
      this.doc.text('** Los precios ya incluyen IVA', startX, y)
      if (ahorro > 0) {
        this.doc.text('** El ahorro corresponde a las ofertas vigentes al momento del pedido', startX, y + 4)
        y += 4
      }
      this.doc.setTextColor(0, 0, 0) // Restaurar color negro

      return y + 20
    }
    return y
  }

  async addFooter(y) {
    let currentY = y

    currentY = this.checkPageBreak(currentY, 80)

    const metodosPago = [
      '- Contado: Transferencia o déposito, tarjeta de crédito, se aceptan hasta 3 visacuotas.',
      '- Crédito: Sujeto a validación de crédito.',
    ]

    const datosAdicionales = [
      '- Para dudas, consultas o reclamos, comunicarse al WhatsApp +502 5800 0282.',
      '- Los precios pueden variar debido a cambios en los costos de materiales.',
    ]

    const cuentasBancos = [
      'BAM - Banco Agromercantil: 30-4020130-0',
      'G&T - Banco G&T Continental: 066-0005267-0',
      'Banrural: 3229073464',
      'BI - Banco Industrial: 092-008713-7',
    ]

    this.doc.setFontSize(10)
    this.doc.setFont('helvetica', 'bold')
    this.doc.text('Métodos de pago:', 10, currentY + 10)
    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(8)
    metodosPago.forEach(metodo => {
      currentY = this.checkPageBreak(currentY, 5)
      this.doc.text(metodo, 10, currentY + 15)
      currentY += 5
    })

    currentY = this.checkPageBreak(currentY, 30)
    this.doc.setFontSize(10)
    this.doc.setFont('helvetica', 'bold')
    this.doc.text('Términos y condiciones:', 10, currentY + 20)

    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(8)

    let datosAdicionalesY = currentY + 25
    datosAdicionales.forEach(dato => {
      datosAdicionalesY = this.checkPageBreak(datosAdicionalesY, 5)
      this.doc.text(dato, 10, datosAdicionalesY)
      datosAdicionalesY += 5
    })

    datosAdicionalesY = this.checkPageBreak(datosAdicionalesY, 30)
    this.doc.setFontSize(10)
    this.doc.setFont('helvetica', 'bold')
    this.doc.text('Depósitos y transferencias en las cuentas monetarias:', 10, datosAdicionalesY + 5)

    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(8)
    let cuentasBancosY = datosAdicionalesY + 10
    cuentasBancos.forEach(dato => {
      cuentasBancosY = this.checkPageBreak(cuentasBancosY, 5)
      this.doc.text(dato, 10, cuentasBancosY)
      cuentasBancosY += 5
    })

    cuentasBancosY = this.checkPageBreak(cuentasBancosY, 10)
    // Agregar línea para firma
    const signatureY = cuentasBancosY + 5
    this.doc.setDrawColor(0, 0, 0)
    this.doc.line(140, signatureY, 200, signatureY)
    this.doc.setFontSize(8)
    this.doc.text('Firma de aprobado', 170, signatureY + 5, { align: 'center' })
  }
}
